import React from "react";
import { Eraser, RefreshCw } from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { FormField } from "../../../../shared/components/forms/FormField";
import type { SelectOption } from "../../../../shared/components/ui/Select";
import {
  convertToBackendDateFormat,
  convertFromBackendDateFormat,
} from "../../../../shared/utils/dateUtils";

export interface SwimmerPreviewFilterProps {
  lifeguardName: string;
  buildingId: string;
  startDate: string;
  endDate: string;
  buildingOptions: SelectOption[];
  onLifeguardNameChange: (value: string) => void;
  onBuildingIdChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  /** Optional: called when user clicks refresh to reload table data */
  onRefresh?: () => void;
  /** Optional: when true, refresh button shows loading state */
  isRefreshing?: boolean;
}

export const SwimmerPreviewFilter: React.FC<SwimmerPreviewFilterProps> = ({
  lifeguardName,
  buildingId,
  startDate,
  endDate,
  buildingOptions,
  onLifeguardNameChange,
  onBuildingIdChange,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  onClearFilters,
  onRefresh,
  isRefreshing = false,
}) => {
  // Convert dates from backend format (DD/MM/YYYY) to HTML input format (YYYY-MM-DD)
  const startDateInputValue = convertFromBackendDateFormat(startDate);
  const endDateInputValue = convertFromBackendDateFormat(endDate);

  // Handle date changes - convert from HTML input format to backend format
  const handleStartDateChange = (value: string) => {
    const backendFormat = convertToBackendDateFormat(value);
    onStartDateChange(backendFormat);
    
    // If end date is set and is before the new start date, clear end date
    if (endDateInputValue && value && endDateInputValue < value) {
      onEndDateChange("");
    }
  };

  const handleEndDateChange = (value: string) => {
    const backendFormat = convertToBackendDateFormat(value);
    onEndDateChange(backendFormat);
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="flex flex-col gap-4 mb-5"
    >
      {/* Responsive three-column grid for filters - Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto w-full">
        <FormField
          type="input"
          label="Nombre del Salvavidas"
          inputProps={{
            type: "text",
            placeholder: "Filtrar por nombre",
            value: lifeguardName,
            onChange: (e) => onLifeguardNameChange(e.target.value),
          }}
        />

        <FormField
          type="searchableSelect"
          label="Unidad Residencial"
          searchableSelectProps={{
            options: buildingOptions,
            value: buildingId,
            onChange: onBuildingIdChange,
            placeholder: "Buscar unidad...",
          }}
        />
        
        {/* Placeholder for alignment if needed, or just let it flow */}
      </div>

      {/* Row 2 - Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto w-full">
        <FormField
          type="input"
          label="Fecha Inicio"
          inputProps={{
            type: "date",
            value: startDateInputValue,
            onChange: (e) => handleStartDateChange(e.target.value),
            max: endDateInputValue || undefined,
            className: "filter-date-input",
          }}
        />

        <FormField
          type="input"
          label="Fecha Fin"
          inputProps={{
            type: "date",
            value: endDateInputValue,
            onChange: (e) => handleEndDateChange(e.target.value),
            min: startDateInputValue || undefined,
            className: "filter-date-input",
          }}
        />
      </div>

      {/* Action buttons: Refresh, Clear, Search */}
      <div className="flex justify-end gap-3 max-w-5xl mx-auto w-full flex-wrap">
        {onRefresh && (
          <Button
            type="button"
            onClick={onRefresh}
            variant="secondary"
            className="h-11 px-4 flex items-center gap-2"
            title="Actualizar datos"
            aria-label="Actualizar datos de la tabla"
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              aria-hidden
            />
            <span>Actualizar</span>
          </Button>
        )}
        <Button
          type="button"
          onClick={onClearFilters}
          variant="secondary"
          className="h-11 px-4 flex items-center gap-2"
          title="Limpiar filtros"
          aria-label="Limpiar filtros"
        >
          <Eraser className="w-4 h-4" />
          <span>Limpiar</span>
        </Button>
        <Button
          type="submit"
          onClick={onApplyFilters}
          variant="primary"
          className="h-11 px-6"
          title="Buscar"
          aria-label="Buscar"
        >
          Buscar
        </Button>
      </div>
    </form>
  );
};
